import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Calculator as CalcIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactBar from "@/components/ContactBar";
import FunnelLinks from "@/components/FunnelLinks";
import NewsletterSignup from "@/components/NewsletterSignup";
import { api, imgUrl, SITE_URL } from "@/lib/api";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setMetaProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => {
      setPost(r.data); setState("ok");
      const d = r.data;
      const metaTitle = d.meta_title || `${d.title} | Zateplenia.eu`;
      const metaDesc = d.meta_description || d.excerpt || d.title;
      const cover = imgUrl(d.cover_image);
      const canonical = `${SITE_URL}/blog/${d.slug}`;
      document.title = metaTitle;
      setMeta("description", metaDesc);
      setCanonical(canonical);
      setMetaProp("og:title", metaTitle);
      setMetaProp("og:description", metaDesc);
      setMetaProp("og:type", "article");
      setMetaProp("og:url", canonical);
      if (cover) setMetaProp("og:image", cover);
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", metaTitle);
      setMeta("twitter:description", metaDesc);
      if (cover) setMeta("twitter:image", cover);
      const ld = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": d.title,
        "description": d.summary || metaDesc,
        "abstract": d.summary || undefined,
        "image": cover ? [cover] : undefined,
        "datePublished": d.created_at,
        "dateModified": d.updated_at || d.created_at,
        "inLanguage": "sk-SK",
        "author": { "@type": "Organization", "name": "Zateplenia.eu", "url": SITE_URL },
        "publisher": {
          "@type": "Organization", "name": "Zateplenia.eu",
          "logo": { "@type": "ImageObject", "url": `${SITE_URL}/favicon.ico` },
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
      };
      let s = document.getElementById("ld-json");
      if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
      s.textContent = JSON.stringify(ld);
    }).catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  if (state === "notfound") return (
    <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center gap-4">
      <h1 className="font-display text-5xl">Článok nenájdený</h1>
      <Link to="/blog" className="text-gold underline">Späť na blog</Link>
    </div>
  );

  return (
    <div>
      <Header />
      <article className="pt-32 pb-16 max-w-[820px] mx-auto px-5 md:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-8"><ArrowLeft className="w-4 h-4" /> Blog</Link>
        <div className="text-gold font-mono2 text-xs uppercase tracking-widest mb-4">{post.category} · {new Date(post.created_at).toLocaleDateString("sk-SK")}</div>
        <h1 className="font-display text-white leading-[0.95] text-4xl sm:text-6xl mb-8">{post.title}</h1>
        <div className="aspect-[16/9] overflow-hidden border border-white/10 mb-10">
          <img src={imgUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
        </div>
        {post.summary && (
          <div className="mb-10 border-l-4 border-gold bg-white/5 pl-5 pr-4 py-4" data-testid="blog-summary">
            <div className="font-mono2 text-gold text-xs uppercase tracking-[0.25em] mb-2">Rýchla odpoveď</div>
            <p className="text-white/90 text-base md:text-lg leading-relaxed">{post.summary}</p>
          </div>
        )}
        <div className="blog-content text-white/80 leading-relaxed space-y-4" data-testid="blog-content"
          dangerouslySetInnerHTML={{ __html: post.content_html }} />

        <div className="mt-12 p-6 border border-gold/30 bg-gold/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="font-display text-2xl text-white">Koľko stojí vaše zateplenie?</div>
            <div className="text-white/60 text-sm">Zistite orientačnú cenu online do 60 sekúnd.</div>
          </div>
          <Link to="/" className="bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2 whitespace-nowrap">
            <CalcIcon className="w-4 h-4" /> Kalkulačka
          </Link>
        </div>
        <div className="mt-6"><ContactBar context={`blog-${slug}`} /></div>
        <div className="mt-10"><NewsletterSignup source={`blog-${slug}`} /></div>
      </article>

      <section className="bg-ink-2 py-20 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-6">Ďalšie kroky</p>
          <FunnelLinks from={`blog-${slug}`} />
        </div>
      </section>
      <Footer />
    </div>
  );
}
